class ProductDiscountTooltip {
    constructor() {
        this.triggerSelector = '[data-product-discount-badge]';
        this.tooltip = null;
        this.currentTrigger = null;
        this.currentText = '';
        this.placement = 'bottom';
        this.boundOnPointerOver = this.onPointerOver.bind(this);
        this.boundOnPointerOut = this.onPointerOut.bind(this);
        this.boundOnViewportChange = this.onViewportChange.bind(this);
        this.boundOnKeyDown = this.onKeyDown.bind(this);
    }

    init() {
        if (!document.querySelector(this.triggerSelector)) {
            return;
        }

        this.createTooltip();

        document.body.addEventListener('pointerover', this.boundOnPointerOver, true);
        document.body.addEventListener('pointerout', this.boundOnPointerOut, true);
        window.addEventListener('scroll', this.boundOnViewportChange, true);
        window.addEventListener('resize', this.boundOnViewportChange);
        document.addEventListener('keydown', this.boundOnKeyDown);
    }

    createTooltip() {
        if (this.tooltip) {
            return;
        }

        const tooltip = document.createElement('div');
        tooltip.className = 'wc-product-discount-tooltip';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.setAttribute('aria-hidden', 'true');
        tooltip.innerHTML = '<div class="wc-product-discount-tooltip__inner text-p-tiny text-neutral-500"></div>';
        document.body.appendChild(tooltip);
        this.tooltip = tooltip;
    }

    getTrigger(target) {
        if (!(target instanceof Element)) {
            return null;
        }

        return target.closest(this.triggerSelector);
    }

    onPointerOver(event) {
        const trigger = this.getTrigger(event.target);
        if (!trigger || trigger === this.currentTrigger) {
            return;
        }

        this.show(trigger);
    }

    onPointerOut(event) {
        if (!this.currentTrigger) {
            return;
        }

        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Element) {
            if (nextTarget.closest(this.triggerSelector) === this.currentTrigger) {
                return;
            }

            if (this.tooltip && this.tooltip.contains(nextTarget)) {
                return;
            }
        }

        this.hide();
    }

    onViewportChange() {
        if (!this.currentTrigger) {
            return;
        }

        this.positionTooltip();
    }

    onKeyDown(event) {
        if (event.key === 'Escape') {
            this.hide();
        }
    }

    show(trigger) {
        if (!(trigger instanceof HTMLElement) || !this.tooltip) {
            return;
        }

        this.currentTrigger = trigger;
        this.currentText = trigger.getAttribute('data-product-discount-tooltip') || '';

        const inner = this.tooltip.querySelector('.wc-product-discount-tooltip__inner');
        if (inner instanceof HTMLElement) {
            inner.textContent = this.currentText;
        }

        this.tooltip.classList.add('is-visible');
        this.tooltip.setAttribute('aria-hidden', 'false');
        this.positionTooltip();
    }

    hide() {
        if (!this.tooltip) {
            return;
        }

        this.tooltip.classList.remove('is-visible');
        this.tooltip.setAttribute('aria-hidden', 'true');
        this.currentTrigger = null;
        this.currentText = '';
    }

    positionTooltip() {
        if (!this.tooltip || !this.currentTrigger) {
            return;
        }

        const triggerRect = this.currentTrigger.getBoundingClientRect();
        const tooltipInner = this.tooltip.querySelector('.wc-product-discount-tooltip__inner');
        if (!(tooltipInner instanceof HTMLElement)) {
            return;
        }

        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const margin = 12;
        const horizontalPadding = 24;
        const maxTooltipWidth = Math.max(160, Math.min(294, viewportWidth - horizontalPadding));

        tooltipInner.style.maxWidth = `${maxTooltipWidth}px`;
        this.tooltip.style.left = '0px';
        this.tooltip.style.top = '0px';
        this.tooltip.dataset.placement = 'bottom';
        this.tooltip.classList.remove('is-above');
        this.tooltip.classList.add('is-below');

        const tooltipRect = tooltipInner.getBoundingClientRect();
        const tooltipWidth = tooltipRect.width || tooltipInner.offsetWidth || 294;
        const tooltipHeight = tooltipRect.height || tooltipInner.offsetHeight || 0;
        const centerLeft = triggerRect.left + (triggerRect.width / 2);
        const minLeft = margin + (tooltipWidth / 2);
        const maxLeft = viewportWidth - margin - (tooltipWidth / 2);
        const left = Math.max(minLeft, Math.min(centerLeft, maxLeft));
        const gap = 10;
        const spaceBelow = viewportHeight - triggerRect.bottom - margin;
        const spaceAbove = triggerRect.top - margin;
        const shouldPlaceAbove = tooltipHeight > 0 && spaceBelow < (tooltipHeight + gap) && spaceAbove > spaceBelow;
        const top = shouldPlaceAbove
            ? triggerRect.top - gap
            : triggerRect.bottom + gap;

        this.placement = shouldPlaceAbove ? 'top' : 'bottom';
        this.tooltip.dataset.placement = this.placement;
        this.tooltip.classList.toggle('is-above', shouldPlaceAbove);
        this.tooltip.classList.toggle('is-below', !shouldPlaceAbove);

        this.tooltip.style.left = `${left}px`;
        this.tooltip.style.top = `${top}px`;
    }
}

const productDiscountTooltip = new ProductDiscountTooltip();
productDiscountTooltip.init();
